import TicketSelector from './TicketSelector';
import serialize from '../../utils/form/serialize';
import StaticBestAvailable from './components/StaticBestAvailable';
import { stringifySpecialRequests } from './TicketSelectorUtils';
import getCustomFormControls from '../../utils/form/getCustomFormControls';
import { PerformanceConfiguration, TicketSelectorOptions } from './types';
import FormHandler, { FormHandlerSubmitCallback } from '../../forms/FormHandler';
import EventsDetailTicketSelectorResponseTypes from './EventsDetailTicketSelectorResponseTypes';
import StateFilteredAddress from '../../forms/address/StateFilteredAddress';
import { StateFilterData } from '../../forms/address/types';
import EventsDetailTicketSelectorFormPostHandler from './EventsDetailTicketSelectorFormPostHandler';
import { IViewItemAnalytics } from '../../analytics/types';
import fireAddToCartAnalytics from '../../analytics/fireAddToCartAnalytics';

interface ReservedPriceTypeAmount {
  priceTypeId: number;
  amount: number;
  quantity: number;
}

export type OnReservationSuccessCallback = (data: { redirectUrl: string, lineItemId: number }) => void;

class EventsDetailTicketSelector extends TicketSelector {
  formHandler: FormHandler;

  constructor(
    $form: HTMLFormElement,
    options: Partial<TicketSelectorOptions>,
    private performanceConfiguration: PerformanceConfiguration,
    stateFilterData: StateFilterData,
    private onReservationSuccess: OnReservationSuccessCallback,
    private viewItemAnalytics?: IViewItemAnalytics,
  ) {
    super($form, options);

    this.formHandler = new FormHandler($form, {
      postHandler: new EventsDetailTicketSelectorFormPostHandler(this, performanceConfiguration.isFromAdditionalItemPage),
      onValidationSuccess: this.onSubmit,
    });

    this.form.addEventListener('submit', this.onSubmitValidateEditablePrice);

    if (stateFilterData) {
      StateFilteredAddress.createForCustomFormFields($form, this.formHandler, stateFilterData);
    }
  }

  onSubmit:FormHandlerSubmitCallback = async ({ stopSubmitting, data }) => {
    let errorMessage:string;

    switch (data.type) {
      case EventsDetailTicketSelectorResponseTypes.Success:
        this.fireAddToCartEvent(data.priceTypeAmounts);
        this.onReservationSuccess({ redirectUrl: data.redirectLocation, lineItemId: data.lineItemId });
        return;

      case EventsDetailTicketSelectorResponseTypes.Error:
      case EventsDetailTicketSelectorResponseTypes.ValidationError:
        errorMessage = data.message as string;
        break;

      default:
        errorMessage = StaticBestAvailable.reservationErrorMessage;
    }

    if (errorMessage) {
      this.formHandler.addErrorMessage(errorMessage, true);
      stopSubmitting();
    }
  };

  private fireAddToCartEvent(priceTypeAmounts?: ReservedPriceTypeAmount[]): void {
    if (!priceTypeAmounts?.length) return;

    const selections = priceTypeAmounts.map((pt) => ({
      price: pt.amount,
      quantity: pt.quantity,
      itemVariant: this.getPriceTypeDescription(pt.priceTypeId),
    }));

    fireAddToCartAnalytics(this.viewItemAnalytics, selections);
  }

  private getPriceTypeDescription(priceTypeId: number): string {
    const data = this.pricetypeQuantities.getData();
    const match = data.allPricetypeQuantities?.find((pt) => pt.pricetypeId === priceTypeId);
    return match?.description || '';
  }

  getPostData() {
    const pricetypeQuantities = this.pricetypeQuantities.getData().allPricetypeQuantities!
      .filter((el) => el.quantity > 0);

    const totalQuantity = pricetypeQuantities.reduce((total, el) => total + el.quantity, 0);

    const specialRequests = this.getSpecialRequests(totalQuantity);

    const zoneId = this.zones!.getSelectedZoneId();

    return {
      ...this.performanceConfiguration,
      zoneId,
      ticketReservationRequests: pricetypeQuantities,
      specialRequests: stringifySpecialRequests(specialRequests),
      customForm: this.getCustomFormData()
    };
  }

  private onSubmitValidateEditablePrice = () => {
    const { isEditablePriceAvailable, isSelectionValid } = this.pricetypes.getData();

    if (isEditablePriceAvailable && !isSelectionValid) {
      this.pricetypes.validateEditablePriceInputs();
    }
  };

  destroy() {
    this.form.removeEventListener('submit', this.onSubmitValidateEditablePrice);
    super.destroy();
  }

  private getCustomFormData() {
    const $controls = getCustomFormControls(this.form);

    const data = serialize($controls);

    return Object.entries(data)
      .map(([key, value]) => ({ key, value }));
  }
}

export default EventsDetailTicketSelector;
